import { equals } from '@bufbuild/protobuf'
import {
  ChevronLastIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsRightIcon,
  Loader2Icon,
  XIcon,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useListProjects, useStartAssessment } from '@/api/assessments.hook'
import {
  useGetCompany,
  useListInherentRiskCategories,
  useSetIRQ,
  useUpdateCompanyInherentRisk,
  useUpdateEngagementInherentRisk,
} from '@/api/company.hook'
import {
  GetCompanyDrawerMetadataResponse,
  IRQAnswerSchema,
} from '@/gen/inventory/v1/company_service_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { riskLevelLabel } from '@/const/label'

import { useTrackCallback } from '@/lib/analytics/events'
import { useHasPermissions } from '@/lib/app/check-permissions.hook'
import {
  answersFromIrqGroups,
  toIrqAnswers,
  useIrqAnswerStore,
} from '@/lib/irq-answer-store/irq-answer-store.hook'
import type { InherentRiskItemsData } from '@/lib/risk/inherent-risk-items.hook'

import { ReprojectOrgCompanyButton } from '@/pages/assessment/irq/reproject-org-company-button'
import { useSubmitIRQ } from '@/pages/assessment/irq/use-submit-irq'
import { PostAssignAssessmentsModal } from '@/pages/company-drawer/company-overview/post-assign-assessments-modal/post-assign-assessments-modal'
import { IrqQuestionCard } from '@/pages/company-drawer/company-overview/set-irq-modal/irq-question-card/irq-question-card'
import { IrqSidebar } from '@/pages/company-drawer/company-overview/set-irq-modal/irq-sidebar/irq-sidebar'
import { SET_IRQ_MODAL_LABEL } from '@/pages/company-drawer/company-overview/set-irq-modal/labels'
import {
  getAvailableSources,
  getDefaultSelectedSourceKeys,
  getSourceGroupKey,
} from '@/pages/company-drawer/company-overview/set-irq-modal/lema-suggestions-card/utils'
import { OverviewStep } from '@/pages/company-drawer/company-overview/set-irq-modal/overview-step/overview-step'
import {
  STEP_KIND,
  type SidebarStep,
} from '@/pages/company-drawer/company-overview/set-irq-modal/types'
import { useIrqValidatedSet } from '@/pages/company-drawer/company-overview/set-irq-modal/use-irq-validated-set.hook'
import {
  buildSidebarSteps,
  computeLocalCategorySeverities,
  computeRiskLevelFromEngagements,
  computeSuggestedRiskLevel,
} from '@/pages/company-drawer/company-overview/set-irq-modal/utils'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { RiskBadge } from '@/components/shared/risk-badge/risk-badge'
import { isEstimatedRisk } from '@/components/shared/risk-badge/utils'

import { SelectInherentRiskStep } from './select-inherent-risk-step'

type FooterProps = {
  onCancel: () => void
  onBack?: () => void
  children: React.ReactNode
}

const Footer = ({ onCancel, onBack, children }: FooterProps) => (
  <div className='px-6 pb-6 pt-4'>
    <div className='flex items-center justify-end gap-4'>
      <Button variant='ghost' onClick={onCancel}>
        {SET_IRQ_MODAL_LABEL.CANCEL}
      </Button>
      {onBack && (
        <Button variant='outline' onClick={onBack}>
          <ChevronLeftIcon className='size-3.5' />
          {SET_IRQ_MODAL_LABEL.BACK}
        </Button>
      )}
      {children}
    </div>
  </div>
)

const toRiskLabel = (level?: RiskLevel) => (level === undefined ? undefined : riskLevelLabel[level])

type SetIrqModalProps = {
  companyId: string
  mode?: 'edit' | 'set'
  from?: string
  engagementModeEnabled: boolean
  engagementId?: string
  engagementName?: string
  inherentRiskData: InherentRiskItemsData
  isIrqTemplateOutdated?: GetCompanyDrawerMetadataResponse['isIrqTemplateOutdated']
  onClose: () => void
  // Fires before close so the parent can open a post-IRQ assessments-selection modal.
  onAssigned?: (riskLevel: RiskLevel) => void
}

export const SetIrqModal = ({
  companyId,
  from,
  mode = 'set',
  engagementModeEnabled,
  engagementId,
  engagementName,
  inherentRiskData,
  onClose,
  onAssigned,
}: SetIrqModalProps) => {
  const isEngagementScoped = !!engagementId
  const isEngagementOnlyAssign = engagementModeEnabled && !isEngagementScoped
  const { data: company } = useGetCompany(companyId)
  const companyName = company?.company?.profile?.name
  const thirdPartyTitle = [companyName, engagementName].filter(Boolean).join(' - ')
  const engagements = useMemo(
    () => company?.company?.orgCompanyEngagements ?? [],
    [company?.company?.orgCompanyEngagements],
  )

  const scopedEngagement = isEngagementScoped
    ? engagements.find((e) => e.id === engagementId)
    : undefined
  const targetName = isEngagementScoped ? (engagementName ?? '') : (companyName ?? '')
  // Entity-level context shared by every IR analytics event so each can be
  // attributed to either the 3rd party or a specific engagement.
  const entityContext = useMemo(
    () => ({
      entityType: isEngagementScoped ? 'engagement' : 'third-party',
      companyId,
      companyName,
      ...(isEngagementScoped && {
        engagementId,
        engagementName,
        ...(scopedEngagement && {
          engagementValidatedRiskLevel: toRiskLabel(scopedEngagement.validatedRiskLevel),
          engagementCalculatedRiskLevel: toRiskLabel(scopedEngagement.calculatedRiskLevel),
        }),
      }),
    }),
    [isEngagementScoped, companyId, companyName, engagementId, engagementName, scopedEngagement],
  )
  // Current saved IR (manual validation). `undefined` until validated.
  const currentRiskLevel = isEngagementScoped
    ? scopedEngagement?.validatedRiskLevel
    : company?.company?.validatedRisk
  const isEstimated = isEstimatedRisk(currentRiskLevel)
  const { projects } = useListProjects()
  const { mutateAsync: startAssessment, isPending: isStartingAssessment } =
    useStartAssessment(companyId)
  const { mutateAsync: updateCompanyInherentRisk } = useUpdateCompanyInherentRisk(companyId)
  const { mutateAsync: setEngagementIrq, isPending: isSettingEngagementIrq } = useSetIRQ(
    companyId,
    engagementId,
  )
  const { mutateAsync: updateEngagementInherentRisk, isPending: isUpdatingEngagement } =
    useUpdateEngagementInherentRisk(companyId)
  const trackAssessmentStart = useTrackCallback('third-party.assessment.start')
  const trackModalOpen = useTrackCallback('third-party.irq.modal.open')
  const trackOverviewStart = useTrackCallback('third-party.irq.overview.start')
  const trackOverviewSkipToAssign = useTrackCallback('third-party.irq.overview.skip-to-assign')
  const trackSourceToggle = useTrackCallback('third-party.irq.overview.source.toggle')
  const trackSidebarNavigate = useTrackCallback('third-party.irq.sidebar.navigate')
  const trackQuestionSkipToAssign = useTrackCallback('third-party.irq.question.skip-to-assign')
  const trackAssignRiskChange = useTrackCallback('third-party.irq.assign.risk-change')
  const trackFinishClick = useTrackCallback('third-party.irq.finish.click')
  const trackEstimatedScoreChange = useTrackCallback('third-party.irq.estimated-score.change')
  const [comment, setComment] = useState('')
  const { submitIRQ, isLoading: isSubmittingIRQ } = useSubmitIRQ(companyId)
  const [selectedRiskLevel, setSelectedRiskLevel] = useState<RiskLevel>(RiskLevel.UNSPECIFIED)
  const [postAssignRiskLevel, setPostAssignRiskLevel] = useState<RiskLevel | null>(null)

  const {
    irqItems,
    lemaItems,
    categoryDisplayOrders,
    isLoading: isLoadingInherentRisk,
  } = inherentRiskData
  const localState = useIrqAnswerStore(irqItems)
  const { data: categories, isLoading: isLoadingCategories } = useListInherentRiskCategories()
  const isLoadingRisk = isLoadingCategories || isLoadingInherentRisk

  const initialIrqAnswers = useMemo(() => {
    if (!isEngagementScoped) return []
    const allGroupIds = new Set(irqItems.map((g) => g.id))
    return toIrqAnswers(answersFromIrqGroups(irqItems), allGroupIds)
  }, [irqItems, isEngagementScoped])

  const currentIrqAnswers = useMemo(
    () => toIrqAnswers(localState.answers, localState.visibleGroupIds),
    [localState.answers, localState.visibleGroupIds],
  )

  const hasUnsavedChanges = useMemo(() => {
    if (!isEngagementScoped) return false
    if (currentIrqAnswers.length !== initialIrqAnswers.length) return true
    return !currentIrqAnswers.every((answer, i) =>
      equals(IRQAnswerSchema, answer, initialIrqAnswers[i]),
    )
  }, [isEngagementScoped, currentIrqAnswers, initialIrqAnswers])

  const availableSources = useMemo(() => getAvailableSources(lemaItems), [lemaItems])
  // Engagement with previously-saved answers should not auto-fill from sources —
  // auto-adding lema items would silently change the saved answer on Save.
  const hasSavedEngagementAnswers = isEngagementScoped && initialIrqAnswers.length > 0
  const defaultSelectedSourceKeys = useMemo(
    () =>
      hasSavedEngagementAnswers
        ? new Set<string>()
        : getDefaultSelectedSourceKeys(lemaItems, isEstimated),
    [lemaItems, isEstimated, hasSavedEngagementAnswers],
  )
  const [userSelectedSourceKeys, setUserSelectedSourceKeys] = useState<Set<string> | null>(null)
  const selectedSourceKeys = userSelectedSourceKeys ?? defaultSelectedSourceKeys
  const allSourcesSelected = selectedSourceKeys.size === availableSources.length

  const filteredLemaItems = useMemo(() => {
    if (allSourcesSelected) return lemaItems
    return lemaItems.map((group) => ({
      ...group,
      inherentRiskItems: group.inherentRiskItems.filter((item) => {
        const key = getSourceGroupKey(item)
        return !key || selectedSourceKeys.has(key)
      }),
    }))
  }, [lemaItems, selectedSourceKeys, allSourcesSelected])

  const localIRQCategorySeverities = useMemo(
    () => computeLocalCategorySeverities(categories, localState),
    [categories, localState],
  )

  const toggleSource = (key: string) => {
    setUserSelectedSourceKeys((prev) => {
      const next = new Set(prev ?? defaultSelectedSourceKeys)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const { fillSuggested, removeSuggested } = localState
  useEffect(() => {
    if (isLoadingRisk) return
    if (categories.length > 0) {
      removeSuggested(lemaItems)
      fillSuggested(filteredLemaItems)
    }
  }, [
    isLoadingRisk,
    categories.length,
    lemaItems,
    filteredLemaItems,
    fillSuggested,
    removeSuggested,
  ])

  useEffect(() => {
    trackModalOpen({
      ...entityContext,
      from,
      mode,
      engagementModeEnabled,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // IR calculated from the current filtered IRQ — updates on answer edits and source toggles.
  // In engagement-only-assign mode the company has no IRQ of its own; the company's IR
  // is the engagement with the highest effective risk (validated overrides calculated).
  const calculatedRiskLevel = useMemo(
    () =>
      isEngagementOnlyAssign
        ? computeRiskLevelFromEngagements(engagements)
        : computeSuggestedRiskLevel({ irqItems, categories, localState }),
    [isEngagementOnlyAssign, engagements, irqItems, categories, localState],
  )

  const riskLevel = useMemo(
    () => (selectedRiskLevel === RiskLevel.UNSPECIFIED ? calculatedRiskLevel : selectedRiskLevel),
    [selectedRiskLevel, calculatedRiskLevel],
  )

  const prevCalculatedRef = useRef<RiskLevel>(RiskLevel.UNSPECIFIED)
  useEffect(() => {
    const prev = prevCalculatedRef.current
    const isValid = (level: RiskLevel) => level !== RiskLevel.UNSPECIFIED
    if (prev !== calculatedRiskLevel && isValid(prev) && isValid(calculatedRiskLevel)) {
      trackEstimatedScoreChange({
        ...entityContext,
        previousScore: riskLevelLabel[prev],
        newScore: riskLevelLabel[calculatedRiskLevel],
      })
    }
    prevCalculatedRef.current = calculatedRiskLevel
  }, [calculatedRiskLevel, trackEstimatedScoreChange, entityContext])

  const hasManagePermissions = useHasPermissions(['base.manage'])

  const steps = useMemo(
    () =>
      buildSidebarSteps({
        irqItems,
        categoryDisplayOrders,
        engagementModeEnabled,
        hasManagePermissions,
        isEngagementScoped,
        visibleGroupIds: localState.visibleGroupIds,
      }),
    [
      irqItems,
      categoryDisplayOrders,
      engagementModeEnabled,
      hasManagePermissions,
      isEngagementScoped,
      localState.visibleGroupIds,
    ],
  )

  const [currentStep, setCurrentStep] = useState(0)
  // Clamp so the index never points past the end if the step list changes underneath us.
  const clampedStep = Math.min(currentStep, steps.length - 1)
  const step = steps[clampedStep]
  const assignIndex = steps.findIndex((s) => s.kind === STEP_KIND.ASSIGN)
  // Inactive conditional questions are shown but not navigable, so they're skipped here.
  const isNavigable = (s: SidebarStep) => s.kind !== STEP_KIND.QUESTION || s.isActive
  const findNavigable = (from: number, dir: 1 | -1) => {
    for (let i = from; i >= 0 && i < steps.length; i += dir) {
      if (isNavigable(steps[i])) return i
    }
    return -1
  }
  const questionIndex = steps.findIndex((s) => s.kind === STEP_KIND.QUESTION && s.isActive)

  const questionSteps = steps.filter(
    (s): s is Extract<SidebarStep, { kind: typeof STEP_KIND.QUESTION }> =>
      s.kind === STEP_KIND.QUESTION && s.isActive,
  )
  const totalQuestions = questionSteps.length
  const currentQuestionPosition =
    step.kind === STEP_KIND.QUESTION ? questionSteps.findIndex((s) => s === step) + 1 : 0

  const validated = useIrqValidatedSet(companyId)

  const markCurrentValidated = () => {
    if (step.kind === STEP_KIND.QUESTION) validated.markValidated(step.group.id)
  }

  const overviewEventProps = {
    ...entityContext,
    suggestedRiskLevel: riskLevelLabel[calculatedRiskLevel],
    excludedSourceCount: availableSources.length - selectedSourceKeys.size,
  }

  const goToAssign = () => {
    if (step.kind === STEP_KIND.OVERVIEW) {
      trackOverviewSkipToAssign(overviewEventProps)
    } else if (step.kind === STEP_KIND.QUESTION) {
      trackQuestionSkipToAssign({
        ...entityContext,
        currentQuestionId: step.group.id,
        currentQuestionPosition,
        totalQuestions,
      })
    }
    if (assignIndex >= 0) setCurrentStep(assignIndex)
  }
  const goToFirstQuestion = () => {
    trackOverviewStart(overviewEventProps)
    if (questionIndex >= 0) setCurrentStep(questionIndex)
  }
  const back = () => {
    markCurrentValidated()
    const prev = findNavigable(clampedStep - 1, -1)
    if (prev >= 0) setCurrentStep(prev)
  }
  const next = () => {
    markCurrentValidated()
    const upcoming = findNavigable(clampedStep + 1, 1)
    if (upcoming >= 0) setCurrentStep(upcoming)
  }

  const onFinish = async () => {
    if (engagementModeEnabled) {
      await updateCompanyInherentRisk({
        id: companyId,
        riskLevel,
        comment: comment || undefined,
      })
    } else {
      const irqAnswers = toIrqAnswers(localState.answers, localState.visibleGroupIds)
      await submitIRQ(riskLevel, calculatedRiskLevel, comment, irqAnswers, entityContext)
    }

    // Orgs without projects auto-start an assessment. Orgs with projects rely on the
    // parent rendering a post-IRQ assessments-selection modal via `onAssigned`.
    if (mode === 'set' && projects.length === 0) {
      const assessment = await startAssessment({ companyId, startAsInProgress: true })
      trackAssessmentStart({
        ...entityContext,
        assessmentId: assessment.ongoingAssessmentId,
        from,
      })
    }

    validated.clear()
  }

  const handleAssignAndFinish = async () => {
    if (!hasManagePermissions) {
      onClose()
      return
    }
    trackFinishClick({
      ...entityContext,
      riskLevel: riskLevelLabel[riskLevel],
      suggestedRiskLevel: riskLevelLabel[calculatedRiskLevel],
      hasComment: comment.trim().length > 0,
      isManualOverride:
        selectedRiskLevel !== RiskLevel.UNSPECIFIED && selectedRiskLevel !== calculatedRiskLevel,
    })
    await onFinish()
    onAssigned?.(riskLevel)
    if (projects.length > 0) {
      setPostAssignRiskLevel(riskLevel)
      return
    }
    onClose()
  }

  const handleSelectedRiskLevelChange = (nextRiskLevel: RiskLevel) => {
    trackAssignRiskChange({
      ...entityContext,
      selectedRiskLevel: riskLevelLabel[nextRiskLevel],
      suggestedRiskLevel: riskLevelLabel[calculatedRiskLevel],
    })
    setSelectedRiskLevel(nextRiskLevel)
  }

  const handleToggleSource = (key: string) => {
    const source = availableSources.find((s) => s.key === key)
    const willExclude = selectedSourceKeys.has(key)
    trackSourceToggle({
      ...entityContext,
      sourceKey: key,
      sourceName: source?.name,
      excluded: willExclude,
    })
    toggleSource(key)
  }

  const handleSidebarClick = (index: number) => {
    const target = steps[index]
    const fromStep = step.kind
    trackSidebarNavigate({
      ...entityContext,
      fromStep,
      toStep: target?.kind,
      toQuestionId: target?.kind === STEP_KIND.QUESTION ? target.group.id : undefined,
      toCategory: target?.kind === STEP_KIND.QUESTION ? target.group.category : undefined,
    })
    markCurrentValidated()
    setCurrentStep(index)
  }

  const handleEngagementFinish = async () => {
    markCurrentValidated()
    trackFinishClick({
      ...entityContext,
      hasUnsavedChanges,
      riskLevel: riskLevelLabel[calculatedRiskLevel],
      suggestedRiskLevel: riskLevelLabel[calculatedRiskLevel],
    })
    if (hasUnsavedChanges) {
      await setEngagementIrq({
        companyId,
        orgCompanyEngagementId: engagementId,
        answers: currentIrqAnswers,
      })
    }
    if (engagementId) {
      await updateEngagementInherentRisk({
        id: engagementId,
        riskLevel,
        comment: comment || undefined,
      })
    }
    validated.clear()
    onClose()
  }

  const isLoading =
    isSubmittingIRQ || isStartingAssessment || isSettingEngagementIrq || isUpdatingEngagement

  if (postAssignRiskLevel !== null) {
    return (
      <PostAssignAssessmentsModal
        companyId={companyId}
        riskLevel={postAssignRiskLevel}
        onClose={() => {
          setPostAssignRiskLevel(null)
          onClose()
        }}
      />
    )
  }

  const headerTitle = isEngagementOnlyAssign
    ? SET_IRQ_MODAL_LABEL.ENGAGEMENT_ENABLED_HEADER
    : SET_IRQ_MODAL_LABEL.HEADER
  const showAssignedRiskInHeader =
    currentRiskLevel !== undefined && step.kind !== STEP_KIND.OVERVIEW
  const showProjectedRiskInHeader =
    !showAssignedRiskInHeader &&
    (step.kind === STEP_KIND.ASSIGN || step.kind === STEP_KIND.QUESTION) &&
    calculatedRiskLevel !== RiskLevel.UNSPECIFIED

  const projectedRiskLevel =
    calculatedRiskLevel === RiskLevel.UNSPECIFIED ? RiskLevel.LOW : calculatedRiskLevel

  return (
    <Modal
      open
      size={isEngagementOnlyAssign ? 'large' : 'xlarge'}
      closable={false}
      footer={null}
      onDismiss={onClose}
      className='[&>div]:!p-0'
    >
      <div className='flex h-[calc(100vh-96px)] max-h-[800px] flex-col'>
        <div className='flex flex-col gap-3 px-6 pb-4 pt-6'>
          <div className='flex items-center gap-4'>
            {thirdPartyTitle && (
              <span className='min-w-0 truncate text-label-large' title={thirdPartyTitle}>
                {thirdPartyTitle}
              </span>
            )}
            <div className='ml-auto flex items-center gap-2'>
              <ReprojectOrgCompanyButton
                orgCompanyId={companyId}
                engagementId={isEngagementScoped ? engagementId : undefined}
              />
              <Button
                variant='ghost'
                size='icon-sm'
                onClick={onClose}
                aria-label={SET_IRQ_MODAL_LABEL.CLOSE}
                className='text-main-foreground-tertiary'
              >
                <XIcon className='size-4' />
              </Button>
            </div>
          </div>
          <div className='flex min-h-9 items-center gap-4'>
            <h2 className='min-w-0 truncate text-main-foreground-primary text-heading-small'>
              {headerTitle}
            </h2>
            {showAssignedRiskInHeader && currentRiskLevel !== undefined && (
              <div className='ml-auto flex items-center gap-2'>
                <span className='text-main-foreground-tertiary text-label-default'>
                  {SET_IRQ_MODAL_LABEL.CALCULATED_IR_WITH_COLON}
                </span>
                <RiskBadge riskLevel={projectedRiskLevel} variant='estimated' />
              </div>
            )}
            {showProjectedRiskInHeader && (
              <div className='ml-auto flex items-center gap-2'>
                <span className='text-main-foreground-tertiary text-label-default'>
                  {SET_IRQ_MODAL_LABEL.CALCULATED_IR_WITH_COLON}
                </span>
                {isLoadingRisk ? (
                  <Loader2Icon className='size-3.5 animate-spin text-main-foreground-tertiary' />
                ) : (
                  <RiskBadge riskLevel={calculatedRiskLevel} variant='estimated' />
                )}
              </div>
            )}
          </div>
        </div>

        <div className='flex flex-1 gap-4 overflow-hidden px-6'>
          {!isEngagementOnlyAssign && (
            <div className='flex w-52 shrink-0 flex-col overflow-hidden'>
              <IrqSidebar
                steps={steps}
                currentIndex={clampedStep}
                onStepClick={handleSidebarClick}
                validatedSet={validated.set}
              />
            </div>
          )}

          <div className='flex-1 overflow-y-auto'>
            {step.kind === STEP_KIND.OVERVIEW && (
              <OverviewStep
                companyId={companyId}
                engagementId={isEngagementScoped ? engagementId : undefined}
                currentRiskLevel={currentRiskLevel ?? RiskLevel.UNSPECIFIED}
                projectedRiskLevel={projectedRiskLevel}
                irqCategorySeverities={localIRQCategorySeverities}
                isEstimated={isEstimated}
                isLoadingRisk={isLoadingRisk}
                sources={availableSources}
                selectedSourceKeys={selectedSourceKeys}
                onToggleSource={handleToggleSource}
              />
            )}
            {step.kind === STEP_KIND.QUESTION && (
              <IrqQuestionCard
                group={step.group}
                lemaGroup={lemaItems.find((g) => g.id === step.group.id)}
                localState={localState}
                currentIndex={currentQuestionPosition}
                totalCount={totalQuestions}
                businessOwnerName={company?.company?.businessOwner?.name}
              />
            )}
            {step.kind === STEP_KIND.ASSIGN && (
              <SelectInherentRiskStep
                currentRiskLevel={currentRiskLevel ?? RiskLevel.UNSPECIFIED}
                suggestedRiskLevel={calculatedRiskLevel}
                comment={comment}
                setComment={setComment}
                setSelectedRiskLevel={handleSelectedRiskLevelChange}
                selectedRiskLevel={selectedRiskLevel}
                irqCategorySeverities={localIRQCategorySeverities}
                isEstimated={isEstimated}
                isLoadingRisk={isLoadingRisk}
                targetName={targetName}
                engagements={isEngagementOnlyAssign ? engagements : undefined}
              />
            )}
          </div>
        </div>

        {step.kind === STEP_KIND.OVERVIEW && (
          <div className='px-6 pb-6 pt-4'>
            <div className='flex items-center justify-end gap-4'>
              {questionIndex < 0 ? (
                hasManagePermissions && (
                  <Button variant='outline' size='lg' onClick={goToAssign}>
                    {SET_IRQ_MODAL_LABEL.CONTINUE_TO_ASSIGN}
                    <ChevronLastIcon className='size-3.5' />
                  </Button>
                )
              ) : (
                <>
                  {hasManagePermissions && (
                    <Button variant='outline' size='lg' onClick={goToAssign}>
                      {SET_IRQ_MODAL_LABEL.SKIP_AND_ASSIGN}
                      <ChevronLastIcon className='size-3.5' />
                    </Button>
                  )}
                  <Button size='lg' onClick={goToFirstQuestion}>
                    {SET_IRQ_MODAL_LABEL.START_VALIDATION}
                    <ChevronRightIcon className='size-3.5' />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
        {step.kind === STEP_KIND.QUESTION && (
          <Footer onCancel={onClose} onBack={back}>
            {hasManagePermissions && (
              <Button variant='outline' onClick={goToAssign}>
                {SET_IRQ_MODAL_LABEL.SKIP_AND_ASSIGN}
                <ChevronsRightIcon className='size-3.5' />
              </Button>
            )}
            <Button onClick={next}>
              {SET_IRQ_MODAL_LABEL.NEXT}
              <ChevronRightIcon className='size-3.5' />
            </Button>
          </Footer>
        )}
        {step.kind === STEP_KIND.ASSIGN && (
          <Footer onCancel={onClose} onBack={isEngagementOnlyAssign ? undefined : back}>
            <Button
              onClick={isEngagementScoped ? handleEngagementFinish : handleAssignAndFinish}
              disabled={
                isLoading ||
                (isEngagementScoped ? riskLevel === RiskLevel.UNSPECIFIED : !hasManagePermissions)
              }
              className='flex items-center gap-1'
            >
              {isLoading && <Loader2Icon className='size-3.5 animate-spin' />}
              {isEngagementScoped || isEngagementOnlyAssign
                ? SET_IRQ_MODAL_LABEL.ASSIGN_INHERENT_RISK
                : SET_IRQ_MODAL_LABEL.ASSIGN_AND_FINISH}
              <ChevronRightIcon className='size-3.5' />
            </Button>
          </Footer>
        )}
      </div>
    </Modal>
  )
}
